import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../Components/Breadcrumb";
import axiosInstance from "../../axiosInterceptor";
import Spinner from "../../Components/spinner";
import { Button } from "react-bootstrap";

const CustomerDetails = () => {
  const navigator = useNavigate();
  const location = useLocation();
  const data = location.state;
  const [customer, setCustomer] = useState(null);
  const [shippingAddress, setShippingAddress] = useState([]);
  const [billingAddress, setBillingAddress] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState("");

  useEffect(() => {
    if (!data?._id) return;
    const fetchAddress = async () => {
      setAddressLoading(true);
      setAddressError("");
      try {
        const res = await axiosInstance.get(
          `adminCustomer/getCustomer/${data._id}`,
        );
        if (res.data?.isSuccess) {
          setCustomer(res.data.data.customer || null);
          setShippingAddress(res.data.data.shippingAddress || []);
          setBillingAddress(res.data.data.billingAddress || []);
        } else {
          setAddressError("No address found for this customer.");
        }
      } catch (err) {
        setAddressError(
          err.response?.data?.message || "Could not load address.",
        );
      } finally {
        setAddressLoading(false);
      }
    };
    fetchAddress();
  }, [data?._id]);

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "Customers", path: "/customers" },
    { label: "Customer Details", path: "*" },
  ];

  const Field = ({ label, value }) => (
    <div className="col-sm-4">
      <div className="form-group">
        <label className="col-form-label fw-semibold">{label}</label>
        <input
          type="text"
          className="form-control border-dark"
          value={value || "-"}
          disabled
          style={{ backgroundColor: "#f8f9fa" }}
        />
      </div>
    </div>
  );

  const SectionTitle = ({ title }) => (
    <div className="col-12 mt-3">
      <p
        className="fw-semibold mb-2"
        style={{
          fontSize: "18px",
          paddingTop: "8px",
          fontWeight: "600",
        }}
      >
        {title}
      </p>
    </div>
  );

  const AddressBlock = ({ address }) => {
    if (!address)
      return (
        <div style={{ padding: "14px 0" }}>
          <p className="text-muted" style={{ fontSize: "13px", margin: 0 }}>
            No address on record.
          </p>
        </div>
      );

    return (
      <div
        style={{
          border: address.isDefault
            ? "1px solid #4fd434ff"
            : "1px solid #e0e0e0",
          borderRadius: "10px",
          padding: "16px 20px",
          background: "#fafafa",
          width: "320px",
          flexShrink: 0,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "11px",
                color: "#888",
                fontWeight: "600",
                textTransform: "uppercase",
                marginBottom: "2px",
              }}
            >
              Name
            </div>
            <div
              style={{ fontSize: "14px", fontWeight: "600", color: "#1a1a1a" }}
            >
              {address.firstName} {address.lastName}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "11px",
                color: "#888",
                fontWeight: "600",
                textTransform: "uppercase",
                marginBottom: "2px",
              }}
            >
              Phone
            </div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {address.phone || "-"}
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #eee", margin: "10px 0" }} />

        <div style={{ marginBottom: "8px", padding: "6px 0px" }}>
          <div
            style={{
              fontSize: "11px",
              color: "#888",
              fontWeight: "600",
              textTransform: "uppercase",
              marginBottom: "2px",
            }}
          >
            Street
          </div>
          <div style={{ fontSize: "14px", color: "#333" }}>
            {address.streetAdd1 || "-"}
          </div>
          {address.streetAdd2 && (
            <div style={{ fontSize: "13px", color: "#555", marginTop: "2px" }}>
              {address.streetAdd2}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "11px",
                color: "#888",
                fontWeight: "600",
                textTransform: "uppercase",
                marginBottom: "2px",
              }}
            >
              City
            </div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {address.city || "-"}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "11px",
                color: "#888",
                fontWeight: "600",
                textTransform: "uppercase",
                marginBottom: "2px",
              }}
            >
              State
            </div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {address.state || "-"}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "11px",
                color: "#888",
                fontWeight: "600",
                textTransform: "uppercase",
                marginBottom: "2px",
              }}
            >
              Zip
            </div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {address.zipCode || "-"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="content-wrapper">
        <div className="page-header mb-3">
          <h3 className="custom-page-title">Customer Details</h3>
          <Breadcrumb items={breadcrumbdata} />
        </div>

        <div className="col-md-12 grid-margin stretch-card p-0">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <SectionTitle title="Customer Information" />
                <Field label="First Name" value={customer?.firstName} />
                <Field label="Last Name" value={customer?.lastName} />
                <Field label="Email" value={customer?.email} />
              </div>

              {addressLoading ? (
                <div className="text-center mt-4">
                  <Spinner />
                </div>
              ) : addressError ? (
                <div className="mt-4">
                  <p
                    className="text-muted text-center"
                    style={{
                      fontSize: "13px",
                      padding: "16px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px dashed #ddd",
                    }}
                  >
                    {addressError}
                  </p>
                </div>
              ) : (
                <>
                  {shippingAddress.length > 0 && (
                    <div className="row mt-3">
                      <SectionTitle title="Shipping Address" />
                      {shippingAddress.length > 0 &&
                        shippingAddress.map((address, index) => (
                          <div class="col-sm-3 mr-4">
                            <AddressBlock address={address} />
                          </div>
                        ))}
                    </div>
                  )}

                  {billingAddress.length > 0 && (
                    <div className="row mt-3">
                      <SectionTitle title="Billing Address" />
                      {billingAddress.length > 0 &&
                        billingAddress.map((address, index) => (
                          <div class="col-sm-3 mr-4">
                            <AddressBlock address={address} />
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div
              className="text-start mb-3"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Button
                color="danger"
                className="mx-2"
                onClick={() =>
                  navigator("/customers", {
                    state: {
                      currentPage: data?.currentPage || 1,
                      perPage: data?.perPage || 10,
                      search: data?.search || "",
                    },
                  })
                }
              >
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerDetails;
